export const Permissions = {
  users: {
    create: "users:create",
    read: "users:read",
    update: "users:update",
    delete: "users:delete",
    ban: "users:ban",
  },
  audit: {
    read: "audit:read",
  },
  system: {
    backup: "system:backup",
  },
  branches: {
    create: "branches:create",
    read: "branches:read",
    update: "branches:update",
    delete: "branches:delete",
  },
  catalog: {
    create: "catalog:create",
    read: "catalog:read",
    update: "catalog:update",
    delete: "catalog:delete",
  },
  pricing: {
    read: "pricing:read",
    update: "pricing:update",
  },
  inventory: {
    create: "inventory:create",
    read: "inventory:read",
    update: "inventory:update",
    delete: "inventory:delete",
    transfer: {
      read: "inventory:transfer:read",
      request: "inventory:transfer:request",
      approve: "inventory:transfer:approve",
      ship: "inventory:transfer:ship",
      receive: "inventory:transfer:receive",
    },
    purchase: {
      read: "inventory:purchase:read",
      create: "inventory:purchase:create",
      approve: "inventory:purchase:approve",
      receive: "inventory:purchase:receive",
      manage: "inventory:purchase:manage",
    },
  },
  orders: {
    read: "orders:read",
  },
  wallet: {
    read: "wallet:read",
    update: "wallet:update",
    approve: "wallet:approve",
  },
  loyalty: {
    read: "loyalty:read",
    update: "loyalty:update",
  },
  membership: {
    read: "membership:read",
    update: "membership:update",
  },
  announcements: {
    read: "announcements:read",
    update: "announcements:update",
  },
  reports: {
    sales: {
      read: "reports:sales:read",
    },
    ops: {
      read: "reports:ops:read",
    },
    wallet: {
      read: "reports:wallet:read",
    },
    inventory: {
      read: "reports:inventory:read",
    },
    export: "reports:export",
  },
  // POS-specific permissions
  pos: {
    // Order Inbox (App Orders)
    orders: {
      view: "orders:view",
      accept: "orders:accept",
      reject: "orders:reject",
      prioritize: "orders:prioritize",
      hold: "orders:hold",
      reorder: "orders:reorder",
      cancel: "orders:cancel",
      update_status: "orders:update_status",
    },
    // Walk-in Sales (Checkout)
    sales: {
      create: "sales:create",
      void: "sales:void",
      apply_discount: "sales:apply_discount",
      apply_coupon: "sales:apply_coupon",
      apply_promo: "sales:apply_promo",
      view: "sales:view",
    },
    // Wallet QR Pay (In-store)
    wallet: {
      scan: "wallet:scan",
      confirm_payment: "wallet:confirm_payment",
      view_transactions: "wallet:view_transactions",
      topup: "wallet:topup",
    },
    // Printing
    printing: {
      print_receipt: "printing:print_receipt",
      print_prep_ticket: "printing:print_prep_ticket",
      reprint: "printing:reprint",
      void_print: "printing:void_print",
      view_settings: "printing:view_settings",
      update_settings: "printing:update_settings",
    },
    // Refund & Exceptions
    refund: {
      cancel_before_accept: "refund:cancel_before_accept",
      request_refund: "refund:request_refund",
      approve_refund: "refund:approve_refund",
      partial_refund: "refund:partial_refund",
      view_requests: "refund:view_requests",
    },
    // Branch Reports
    reports: {
      view_daily_sales: "reports:view_daily_sales",
      view_top_products: "reports:view_top_products",
      view_wallet_payments: "reports:view_wallet_payments",
      view_order_throughput: "reports:view_order_throughput",
      view_all: "reports:view_all",
    },
    // System Settings (Branch Manager only)
    settings: {
      view: "settings:view",
      update: "settings:update",
      manage_printers: "settings:manage_printers",
      manage_staff: "settings:manage_staff",
    },
    // Inventory (if needed)
    inventory: {
      view: "inventory:view",
      update: "inventory:update",
      adjust: "inventory:adjust",
      transfer: {
        read: "inventory:transfer:read",
        request: "inventory:transfer:request",
        receive: "inventory:transfer:receive",
      },
    },
    // Catalog/Products (read-only for POS)
    catalog: {
      view: "catalog:view",
      view_prices: "catalog:view_prices",
    },
    // Customers
    customers: {
      view: "customers:view",
      create: "customers:create",
      update: "customers:update",
      view_loyalty: "customers:view_loyalty",
    },
  },
} as const;

export const ALL_PERMISSIONS = [
  // Users
  { id: Permissions.users.create, resource: "users", action: "create" },
  { id: Permissions.users.read, resource: "users", action: "read" },
  { id: Permissions.users.update, resource: "users", action: "update" },
  { id: Permissions.users.delete, resource: "users", action: "delete" },
  { id: Permissions.users.ban, resource: "users", action: "ban" },
  // Audit
  { id: Permissions.audit.read, resource: "audit", action: "read" },
  // System
  { id: Permissions.system.backup, resource: "system", action: "backup" },
  // Branches
  { id: Permissions.branches.create, resource: "branches", action: "create" },
  { id: Permissions.branches.read, resource: "branches", action: "read" },
  { id: Permissions.branches.update, resource: "branches", action: "update" },
  { id: Permissions.branches.delete, resource: "branches", action: "delete" },
  // Catalog
  { id: Permissions.catalog.create, resource: "catalog", action: "create" },
  { id: Permissions.catalog.read, resource: "catalog", action: "read" },
  { id: Permissions.catalog.update, resource: "catalog", action: "update" },
  { id: Permissions.catalog.delete, resource: "catalog", action: "delete" },
  // Pricing
  { id: Permissions.pricing.read, resource: "pricing", action: "read" },
  { id: Permissions.pricing.update, resource: "pricing", action: "update" },
  // Inventory
  { id: Permissions.inventory.create, resource: "inventory", action: "create" },
  { id: Permissions.inventory.read, resource: "inventory", action: "read" },
  { id: Permissions.inventory.update, resource: "inventory", action: "update" },
  { id: Permissions.inventory.delete, resource: "inventory", action: "delete" },
  {
    id: Permissions.inventory.transfer.read,
    resource: "inventory.transfer",
    action: "read",
  },
  {
    id: Permissions.inventory.transfer.request,
    resource: "inventory.transfer",
    action: "request",
  },
  {
    id: Permissions.inventory.transfer.approve,
    resource: "inventory.transfer",
    action: "approve",
  },
  {
    id: Permissions.inventory.transfer.ship,
    resource: "inventory.transfer",
    action: "ship",
  },
  {
    id: Permissions.inventory.transfer.receive,
    resource: "inventory.transfer",
    action: "receive",
  },
  {
    id: Permissions.inventory.purchase.read,
    resource: "inventory.purchase",
    action: "read",
  },
  {
    id: Permissions.inventory.purchase.create,
    resource: "inventory.purchase",
    action: "create",
  },
  {
    id: Permissions.inventory.purchase.approve,
    resource: "inventory.purchase",
    action: "approve",
  },
  {
    id: Permissions.inventory.purchase.receive,
    resource: "inventory.purchase",
    action: "receive",
  },
  {
    id: Permissions.inventory.purchase.manage,
    resource: "inventory.purchase",
    action: "manage",
  },
  // Orders
  { id: Permissions.orders.read, resource: "orders", action: "read" },
  // Wallet
  { id: Permissions.wallet.read, resource: "wallet", action: "read" },
  { id: Permissions.wallet.update, resource: "wallet", action: "update" },
  { id: Permissions.wallet.approve, resource: "wallet", action: "approve" },
  // Loyalty
  { id: Permissions.loyalty.read, resource: "loyalty", action: "read" },
  { id: Permissions.loyalty.update, resource: "loyalty", action: "update" },
  { id: Permissions.membership.read, resource: "membership", action: "read" },
  {
    id: Permissions.membership.update,
    resource: "membership",
    action: "update",
  },
  // Announcements
  {
    id: Permissions.announcements.read,
    resource: "announcements",
    action: "read",
  },
  {
    id: Permissions.announcements.update,
    resource: "announcements",
    action: "update",
  },
  // Reports
  {
    id: Permissions.reports.sales.read,
    resource: "reports.sales",
    action: "read",
  },
  { id: Permissions.reports.ops.read, resource: "reports.ops", action: "read" },
  {
    id: Permissions.reports.wallet.read,
    resource: "reports.wallet",
    action: "read",
  },
  {
    id: Permissions.reports.inventory.read,
    resource: "reports.inventory",
    action: "read",
  },
  { id: Permissions.reports.export, resource: "reports", action: "export" },
  // POS-specific permissions
  // Order Inbox (App Orders)
  {
    id: Permissions.pos.orders.view,
    resource: "orders",
    action: "view",
    pos: true,
  },
  {
    id: Permissions.pos.orders.accept,
    resource: "orders",
    action: "accept",
    pos: true,
  },
  {
    id: Permissions.pos.orders.reject,
    resource: "orders",
    action: "reject",
    pos: true,
  },
  {
    id: Permissions.pos.orders.prioritize,
    resource: "orders",
    action: "prioritize",
    pos: true,
  },
  {
    id: Permissions.pos.orders.hold,
    resource: "orders",
    action: "hold",
    pos: true,
  },
  {
    id: Permissions.pos.orders.reorder,
    resource: "orders",
    action: "reorder",
    pos: true,
  },
  {
    id: Permissions.pos.orders.cancel,
    resource: "orders",
    action: "cancel",
    pos: true,
  },
  {
    id: Permissions.pos.orders.update_status,
    resource: "orders",
    action: "update_status",
    pos: true,
  },
  // Walk-in Sales (Checkout)
  {
    id: Permissions.pos.sales.create,
    resource: "sales",
    action: "create",
    pos: true,
  },
  {
    id: Permissions.pos.sales.void,
    resource: "sales",
    action: "void",
    pos: true,
  },
  {
    id: Permissions.pos.sales.apply_discount,
    resource: "sales",
    action: "apply_discount",
    pos: true,
  },
  {
    id: Permissions.pos.sales.apply_coupon,
    resource: "sales",
    action: "apply_coupon",
    pos: true,
  },
  {
    id: Permissions.pos.sales.apply_promo,
    resource: "sales",
    action: "apply_promo",
    pos: true,
  },
  {
    id: Permissions.pos.sales.view,
    resource: "sales",
    action: "view",
    pos: true,
  },
  // Wallet QR Pay (In-store)
  {
    id: Permissions.pos.wallet.scan,
    resource: "wallet",
    action: "scan",
    pos: true,
  },
  {
    id: Permissions.pos.wallet.confirm_payment,
    resource: "wallet",
    action: "confirm_payment",
    pos: true,
  },
  {
    id: Permissions.pos.wallet.view_transactions,
    resource: "wallet",
    action: "view_transactions",
    pos: true,
  },
  {
    id: Permissions.pos.wallet.topup,
    resource: "wallet",
    action: "topup",
    pos: true,
  },
  // Printing
  {
    id: Permissions.pos.printing.print_receipt,
    resource: "printing",
    action: "print_receipt",
    pos: true,
  },
  {
    id: Permissions.pos.printing.print_prep_ticket,
    resource: "printing",
    action: "print_prep_ticket",
    pos: true,
  },
  {
    id: Permissions.pos.printing.reprint,
    resource: "printing",
    action: "reprint",
    pos: true,
  },
  {
    id: Permissions.pos.printing.void_print,
    resource: "printing",
    action: "void_print",
    pos: true,
  },
  {
    id: Permissions.pos.printing.view_settings,
    resource: "printing",
    action: "view_settings",
    pos: true,
  },
  {
    id: Permissions.pos.printing.update_settings,
    resource: "printing",
    action: "update_settings",
    pos: true,
  },
  // Refund & Exceptions
  {
    id: Permissions.pos.refund.cancel_before_accept,
    resource: "refund",
    action: "cancel_before_accept",
    pos: true,
  },
  {
    id: Permissions.pos.refund.request_refund,
    resource: "refund",
    action: "request_refund",
    pos: true,
  },
  {
    id: Permissions.pos.refund.approve_refund,
    resource: "refund",
    action: "approve_refund",
    pos: true,
  },
  {
    id: Permissions.pos.refund.partial_refund,
    resource: "refund",
    action: "partial_refund",
    pos: true,
  },
  {
    id: Permissions.pos.refund.view_requests,
    resource: "refund",
    action: "view_requests",
    pos: true,
  },
  // Branch Reports
  {
    id: Permissions.pos.reports.view_daily_sales,
    resource: "reports",
    action: "view_daily_sales",
    pos: true,
  },
  {
    id: Permissions.pos.reports.view_top_products,
    resource: "reports",
    action: "view_top_products",
    pos: true,
  },
  {
    id: Permissions.pos.reports.view_wallet_payments,
    resource: "reports",
    action: "view_wallet_payments",
    pos: true,
  },
  {
    id: Permissions.pos.reports.view_order_throughput,
    resource: "reports",
    action: "view_order_throughput",
    pos: true,
  },
  {
    id: Permissions.pos.reports.view_all,
    resource: "reports",
    action: "view_all",
    pos: true,
  },
  // System Settings (Branch Manager only)
  {
    id: Permissions.pos.settings.view,
    resource: "settings",
    action: "view",
    pos: true,
  },
  {
    id: Permissions.pos.settings.update,
    resource: "settings",
    action: "update",
    pos: true,
  },
  {
    id: Permissions.pos.settings.manage_printers,
    resource: "settings",
    action: "manage_printers",
    pos: true,
  },
  {
    id: Permissions.pos.settings.manage_staff,
    resource: "settings",
    action: "manage_staff",
    pos: true,
  },
  // Inventory (if needed)
  {
    id: Permissions.pos.inventory.view,
    resource: "inventory",
    action: "view",
    pos: true,
  },
  {
    id: Permissions.pos.inventory.update,
    resource: "inventory",
    action: "update",
    pos: true,
  },
  {
    id: Permissions.pos.inventory.adjust,
    resource: "inventory",
    action: "adjust",
    pos: true,
  },
  {
    id: Permissions.pos.inventory.transfer.read,
    resource: "inventory.transfer",
    action: "read",
    pos: true,
  },
  {
    id: Permissions.pos.inventory.transfer.request,
    resource: "inventory.transfer",
    action: "request",
    pos: true,
  },
  {
    id: Permissions.pos.inventory.transfer.receive,
    resource: "inventory.transfer",
    action: "receive",
    pos: true,
  },
  // Catalog/Products (read-only for POS)
  {
    id: Permissions.pos.catalog.view,
    resource: "catalog",
    action: "view",
    pos: true,
  },
  {
    id: Permissions.pos.catalog.view_prices,
    resource: "catalog",
    action: "view_prices",
    pos: true,
  },
  // Customers
  {
    id: Permissions.pos.customers.view,
    resource: "customers",
    action: "view",
    pos: true,
  },
  {
    id: Permissions.pos.customers.create,
    resource: "customers",
    action: "create",
    pos: true,
  },
  {
    id: Permissions.pos.customers.update,
    resource: "customers",
    action: "update",
    pos: true,
  },
  {
    id: Permissions.pos.customers.view_loyalty,
    resource: "customers",
    action: "view_loyalty",
    pos: true,
  },
] as const;

export type PermissionId = (typeof ALL_PERMISSIONS)[number]["id"];

// Human-friendly labels for rendering in UI
export const RESOURCE_LABELS: Record<string, string> = {
  users: "ຜູ້ໃຊ້",
  audit: "ບັນທຶກການກວດກາ",
  branches: "ສາຂາ",
  catalog: "ແຄຕາໂລກ",
  pricing: "ລາຄາຕໍ່ສາຂາ",
  inventory: "ສາງ / ວັດຖຸດິບ",
  "inventory.transfer": "ເບິກສຕັອກສາຂາ",
  "inventory.purchase": "ໃບສັ່ງຊື້ (PO)",
  orders: "ອໍເດີ",
  wallet: "Wallet",
  loyalty: "ນະໂຍບາຍການເກັບແຕ້ມ",
  membership: "ບັດສະມາຊິກ",
  announcements: "ຂ່າວສານ",
  "reports.sales": "ລາຍງານການຂາຍ",
  "reports.ops": "ລາຍງານການດຳເນີນງານ",
  "reports.wallet": "ລາຍງານ Wallet",
  "reports.inventory": "ລາຍງານສາງ",
  reports: "ລາຍງານ",
  sales: "ການຂາຍ",
  printing: "ການພິມ",
  refund: "ການຄືນເງິນ",
  settings: "ການຕັ້ງຄ່າ",
  customers: "ລູກຄ້າ",
  system: "ລະບົບ",
};

export const ACTION_LABELS: Record<string, string> = {
  create: "ສ້າງ",
  read: "ເບິ່ງ",
  update: "ແກ້ໄຂ",
  delete: "ລຶບ",
  ban: "ລະງັບ",
  all: "ທັງໝົດ",
  view: "ເບິ່ງ",
  accept: "ຍອມຮັບ",
  reject: "ປະຕິເສດ",
  prioritize: "ຈັດລຳດັບຄວາມສຳຄັນ",
  hold: "ຢຸດຊົ່ວຄາວ",
  reorder: "ຈັດລຳດັບໃໝ່",
  cancel: "ຍົກເລີກ",
  update_status: "ອັບເດດສະຖານະ",
  void: "ຍົກເລີກ",
  apply_discount: "ໃຊ້ສ່ວນຫຼຸດ",
  apply_coupon: "ໃຊ້ຄູປອງ",
  apply_promo: "ໃຊ້ໂປຣໂມ",
  scan: "ສະແກນ",
  confirm_payment: "ຢືນຢັນການຊຳລະ",
  view_transactions: "ເບິ່ງການເຮັດທຸລະກຳ",
  print_receipt: "ພິມໃບບິນ",
  print_prep_ticket: "ພິມໃບກຳລັງກະກຽມ",
  reprint: "ພິມໃໝ່",
  void_print: "ຍົກເລີກການພິມ",
  view_settings: "ເບິ່ງການຕັ້ງຄ່າ",
  update_settings: "ອັບເດດການຕັ້ງຄ່າ",
  cancel_before_accept: "ຍົກເລີກກ່ອນຍອມຮັບ",
  request_refund: "ຂໍຄືນເງິນ",
  approve_refund: "ອະນຸມັດການຄືນເງິນ",
  partial_refund: "ຄືນເງິນບາງສ່ວນ",
  view_requests: "ເບິ່ງຄຳຂໍ",
  view_daily_sales: "ເບິ່ງການຂາຍປະຈຳວັນ",
  view_top_products: "ເບິ່ງສິນຄ້າຍອດນິຍົມ",
  view_wallet_payments: "ເບິ່ງການຊຳລະ Wallet",
  view_order_throughput: "ເບິ່ງປະສິດທິພາບອໍເດີ",
  view_all: "ເບິ່ງທັງໝົດ",
  manage_printers: "ຈັດການເຄື່ອງພິມ",
  manage_staff: "ຈັດການພະນັກງານ",
  adjust: "ປັບແກ້",
  view_prices: "ເບິ່ງລາຄາ",
  view_loyalty: "ເບິ່ງລາຍການເກັບແຕ້ມ",
  topup: "ຢືນຢັນການເຕີມທີ່ເຄົາເຕີ",
  backup: "ສຳຮອງຂໍ້ມູນ (ດາວໂຫລດ)",
  request: "ສົ່ງຄຳຂໍ",
  approve: "ອະນຸມັດ",
  ship: "ຈັດສົ່ງ",
  receive: "ຮັບສຕັອກ",
  manage: "ຈັດການ",
};

export function getResourceLabel(resource: string): string {
  return RESOURCE_LABELS[resource] ?? resource;
}

export function getActionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

export function getPermissionLabel(id: PermissionId): string {
  const [resource, action] = (id as string).split(":");
  return `${getActionLabel(action ?? "")} ${getResourceLabel(resource ?? "")}`;
}

export function getPermissionLabels(ids: PermissionId[]): string[] {
  return ids.map((id) => getPermissionLabel(id));
}
